import type {
    Device,
    DeviceCategory,
    Rack
} from '@/types'

import { groupDevicesByRack, toCategory } from '@/lib/inventory'

// Pure geometry for the data hall. Units are metres. Racks stand in rows along the
// z axis; two facing rows form a pod around a contained cold aisle. The entrance
// is in the +z wall, so rack position 1 is the end of each row closest to the door.

export const U = 0.04445
export const RACK_W = 0.6
export const RACK_D = 1.07
export const RACK_BASE = 0.075
export const RACK_TOP = 0.06
export const DEVICE_W = 0.482
export const DEVICE_D = 0.76
export const DEVICE_INSET = 0.07
export const COLD_AISLE = 1.2
export const HOT_AISLE = 1.2
export const CEILING = 4.2
export const DOOR_WIDTH = 1.9
export const DOOR_HEIGHT = 2.5
export const PULL_OUT = 0.5
export const TRAY_Y = 2.62
export const RUNNER_Y = 2.92

export type Vec3 = [number, number, number]

export const rackHeight = (units: number): number => RACK_BASE + units * U + RACK_TOP

export type RackLayout = {
    rack: Rack
    index: number
    rowIndex: number
    podIndex: number
    side: 0 | 1
    x: number
    z: number
    rotationY: number
    normal: [number, number]
    lateral: [number, number]
    height: number
    category: DeviceCategory
    devices: Device[]
}

export type RowLayout = {
    label: string
    index: number
    podIndex: number
    side: 0 | 1
    x: number
    zMin: number
    zMax: number
    category: DeviceCategory
    rackIndices: number[]
}

export type PodLayout = {
    index: number
    x: number
    rows: number[]
    zMin: number
    zMax: number
    contained: boolean
}

export type HallBounds = {
    minX: number
    maxX: number
    minZ: number
    maxZ: number
}

export type HallLayout = {
    racks: RackLayout[]
    rows: RowLayout[]
    pods: PodLayout[]
    hotAisles: { x: number, zMin: number, zMax: number }[]
    bounds: HallBounds
    rackArea: HallBounds
    door: { x: number, z: number }
    center: { x: number, z: number }
    rackById: Map<string, RackLayout>
    rackOfDevice: Map<string, RackLayout>
}

const POD_SPACING = 2 * RACK_D + COLD_AISLE + HOT_AISLE

const dominantCategory = (racks: RackLayout[]): DeviceCategory => {
    const counts: Record<DeviceCategory, number> = { server: 0, network: 0, storage: 0 }

    for (const rack of racks) counts[rack.category] += 1

    return (Object.keys(counts) as DeviceCategory[]).reduce((best, key) => counts[key] > counts[best] ? key : best, 'server')
}

export const buildHallLayout = (racks: Rack[], devices: Device[]): HallLayout => {
    const byRack = groupDevicesByRack(devices)
    const labels = Array.from(new Set(racks.map(rack => rack.row_label))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    const podCount = Math.max(1, Math.ceil(labels.length / 2))
    const maxPosition = racks.reduce((max, rack) => Math.max(max, rack.position), 1)

    const sortedRacks = [...racks].sort((a, b) => {
        const row = labels.indexOf(a.row_label) - labels.indexOf(b.row_label)

        return row !== 0 ? row : a.position - b.position
    })

    const rackLayouts: RackLayout[] = sortedRacks.map((rack, index) => {
        const rowIndex = labels.indexOf(rack.row_label)
        const podIndex = Math.floor(rowIndex / 2)
        const side = (rowIndex % 2) as 0 | 1
        const podX = (podIndex - (podCount - 1) / 2) * POD_SPACING
        const offset = COLD_AISLE / 2 + RACK_D / 2
        const x = side === 0 ? podX - offset : podX + offset
        const rotationY = side === 0 ? Math.PI / 2 : -Math.PI / 2
        const z = ((maxPosition - 1) / 2 - (rack.position - 1)) * RACK_W
        const zoneCategory = rack.zone.toLowerCase().startsWith('comp') ? 'server' : toCategory(rack.zone)

        return {
            rack,
            index,
            rowIndex,
            podIndex,
            side,
            x,
            z,
            rotationY,
            normal: [Math.sin(rotationY), Math.cos(rotationY)],
            lateral: [Math.cos(rotationY), -Math.sin(rotationY)],
            height: rackHeight(rack.units),
            category: zoneCategory,
            devices: byRack.get(rack.id) ?? []
        }
    })

    const rows: RowLayout[] = labels.map((label, index) => {
        const members = rackLayouts.filter(rack => rack.rowIndex === index)
        const zs = members.map(rack => rack.z)
        const podIndex = Math.floor(index / 2)
        const side = (index % 2) as 0 | 1

        return {
            label,
            index,
            podIndex,
            side,
            x: members[0]?.x ?? 0,
            zMin: (zs.length ? Math.min(...zs) : 0) - RACK_W / 2,
            zMax: (zs.length ? Math.max(...zs) : 0) + RACK_W / 2,
            category: dominantCategory(members),
            rackIndices: members.map(rack => rack.index)
        }
    })

    const pods: PodLayout[] = Array.from({ length: Math.ceil(labels.length / 2) }, (_, index) => {
        const podRows = rows.filter(row => row.podIndex === index)

        return {
            index,
            x: (index - (podCount - 1) / 2) * POD_SPACING,
            rows: podRows.map(row => row.index),
            zMin: Math.min(...podRows.map(row => row.zMin)),
            zMax: Math.max(...podRows.map(row => row.zMax)),
            contained: podRows.length === 2
        }
    })

    const hasRacks = rackLayouts.length > 0
    const rackArea: HallBounds = hasRacks ? {
        minX: Math.min(...rows.map(row => row.x)) - RACK_D / 2,
        maxX: Math.max(...rows.map(row => row.x)) + RACK_D / 2,
        minZ: Math.min(...rows.map(row => row.zMin)),
        maxZ: Math.max(...rows.map(row => row.zMax))
    } : { minX: -2, maxX: 2, minZ: -2, maxZ: 2 }

    const hotAisles: HallLayout['hotAisles'] = []

    if (hasRacks) {
        const edge = COLD_AISLE / 2 + RACK_D + HOT_AISLE / 2

        hotAisles.push({ x: pods[0].x - edge, zMin: rackArea.minZ, zMax: rackArea.maxZ })

        for (let i = 0; i < pods.length - 1; i++) {
            hotAisles.push({ x: (pods[i].x + pods[i + 1].x) / 2, zMin: rackArea.minZ, zMax: rackArea.maxZ })
        }

        const last = pods[pods.length - 1]

        if (last.contained) hotAisles.push({ x: last.x + edge, zMin: rackArea.minZ, zMax: rackArea.maxZ })
    }

    let minX = rackArea.minX - 2.8
    let maxX = rackArea.maxX + 2.8
    let minZ = rackArea.minZ - 2.8
    let maxZ = rackArea.maxZ + 4.2

    if (maxX - minX < 12) {
        const grow = (12 - (maxX - minX)) / 2

        minX -= grow
        maxX += grow
    }

    if (maxZ - minZ < 11) {
        const grow = (11 - (maxZ - minZ)) / 2

        minZ -= grow
        maxZ += grow
    }

    const rackById = new Map(rackLayouts.map(rack => [rack.rack.id, rack]))
    const rackOfDevice = new Map<string, RackLayout>()

    for (const rack of rackLayouts) {
        for (const device of rack.devices) rackOfDevice.set(device.id, rack)
    }

    return {
        racks: rackLayouts,
        rows,
        pods,
        hotAisles,
        bounds: { minX, maxX, minZ, maxZ },
        rackArea,
        door: { x: 0, z: maxZ },
        center: { x: (rackArea.minX + rackArea.maxX) / 2, z: (rackArea.minZ + rackArea.maxZ) / 2 },
        rackById,
        rackOfDevice
    }
}

// Rack-local to world transform (rack origin is the floor centre of the rack).
export const rackToWorld = (rack: RackLayout, lx: number, ly: number, lz: number): Vec3 => {
    const cos = Math.cos(rack.rotationY)
    const sin = Math.sin(rack.rotationY)

    return [rack.x + lx * cos + lz * sin, ly, rack.z - lx * sin + lz * cos]
}

// Where a device sits inside its rack, clamped so bad data never pokes out of the frame.
export const deviceSlot = (device: Pick<Device, 'u_position' | 'u_height'>, units: number) => {
    const height = Math.min(Math.max(1, device.u_height), units)
    const start = Math.min(Math.max(1, device.u_position), units - height + 1)

    return { start, height }
}

export const deviceLocalCenter = (start: number, height: number): Vec3 => {
    const y = RACK_BASE + (start - 1) * U + (height * U) / 2
    const z = RACK_D / 2 - DEVICE_INSET - DEVICE_D / 2

    return [0, y, z]
}

export const oppositeRowIndex = (layout: HallLayout, rack: RackLayout): number | null => {
    const pod = layout.pods[rack.podIndex]

    if (!pod || !pod.contained) return null

    const other = pod.rows.find(row => row !== rack.rowIndex)

    return other ?? null
}

export type CameraView = {
    position: Vec3
    target: Vec3
    fov: number
    approach?: Vec3[]
}

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s]

const normalize = (a: Vec3): Vec3 => {
    const length = Math.hypot(a[0], a[1], a[2]) || 1

    return [a[0] / length, a[1] / length, a[2] / length]
}

export const introStartView = (layout: HallLayout): CameraView => ({
    position: [layout.door.x + 0.6, 1.72, layout.door.z + 7.6],
    target: [layout.door.x, 1.5, layout.door.z],
    fov: 46
})

export const introPath = (layout: HallLayout, aspect: number): CameraView => {
    const overview = overviewView(layout, aspect)
    const { door } = layout

    return {
        ...overview,
        approach: [
            [door.x + 0.3, 1.7, door.z + 3.2],
            [door.x, 1.68, door.z + 0.6],
            [door.x, 1.75, door.z - 1.8],
            [door.x + 0.6, 3.4, door.z - 2.6]
        ]
    }
}

export const overviewView = (layout: HallLayout, aspect: number): CameraView => {
    const { bounds, center } = layout
    const span = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ)
    const fit = aspect < 1 ? 1.2 + (1 - aspect) * 1.3 : 1
    const distance = span * 0.95 * fit
    const direction = normalize([0.42, 0.8, 0.43])
    const target: Vec3 = [center.x, 0.2, center.z - 0.4]

    return {
        position: add(target, scale(direction, distance)),
        target,
        fov: 42
    }
}

export const zoneView = (layout: HallLayout, category: DeviceCategory, aspect: number): CameraView => {
    let members = layout.racks.filter(rack => rack.category === category)

    if (!members.length) {
        members = layout.racks.filter(rack => rack.devices.some(device => toCategory(device.category) === category))
    }

    if (!members.length) return overviewView(layout, aspect)

    const podCounts = new Map<number, number>()

    for (const rack of members) podCounts.set(rack.podIndex, (podCounts.get(rack.podIndex) ?? 0) + 1)

    const podIndex = [...podCounts.entries()].sort((a, b) => b[1] - a[1])[0][0]
    const inPod = members.filter(rack => rack.podIndex === podIndex)
    const pod = layout.pods[podIndex]
    const sides = new Set(inPod.map(rack => rack.side))
    const zMax = Math.max(...inPod.map(rack => rack.z)) + RACK_W / 2
    const zMin = Math.min(...inPod.map(rack => rack.z)) - RACK_W / 2

    let lateral = 0
    let targetX = pod.x

    if (sides.size === 1) {
        const side = [...sides][0]

        lateral = side === 0 ? 0.32 : -0.32
        targetX = side === 0 ? pod.x - 0.55 : pod.x + 0.55
    }

    const inside = Math.min(1.1, (zMax - zMin) * 0.25)
    const position: Vec3 = [pod.x + lateral, 1.62, zMax - inside]
    const target: Vec3 = [targetX, 1.08, zMin + (zMax - zMin) * 0.12]

    return {
        position,
        target,
        fov: aspect < 1 ? 66 : 56,
        approach: [
            [pod.x + lateral * 0.5, 2.3, zMax + 4.2],
            [pod.x + lateral, 1.7, zMax + 1.6]
        ]
    }
}

export const rackFront = (rack: RackLayout, y: number): Vec3 => rackToWorld(rack, 0, y, RACK_D / 2)

export const rackView = (rack: RackLayout, aspect: number): CameraView => {
    const front = rackFront(rack, rack.height * 0.5)
    const [nx, nz] = rack.normal
    const [lx, lz] = rack.lateral
    const distance = aspect < 1 ? 3.6 : 2.65
    const position: Vec3 = [front[0] + nx * distance + lx * 0.28, front[1] + 0.42, front[2] + nz * distance + lz * 0.28]
    const target: Vec3 = [front[0], front[1] - 0.05, front[2]]

    return {
        position,
        target,
        fov: 50,
        approach: [[position[0], 3.1, position[2]]]
    }
}

export const deviceView = (rack: RackLayout, device: Device, aspect: number): CameraView => {
    const { start, height } = deviceSlot(device, rack.rack.units)
    const local = deviceLocalCenter(start, height)
    const frontZ = local[2] + DEVICE_D / 2 + PULL_OUT
    const center = rackToWorld(rack, 0, local[1], frontZ)
    const [nx, nz] = rack.normal
    const [lx, lz] = rack.lateral
    const deviceHeight = height * U
    const distance = Math.max(0.95, deviceHeight * 1.7 + 0.55) * (aspect < 1 ? 1.45 : 1)
    const position: Vec3 = [center[0] + nx * distance + lx * 0.26, center[1] + 0.2, center[2] + nz * distance + lz * 0.26]
    const target: Vec3 = [center[0] - nx * 0.18, center[1] - 0.02, center[2] - nz * 0.18]

    return {
        position,
        target,
        fov: 46
    }
}
